import PropTypes from 'prop-types';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';

import SectionContainer from '../SectionContainer';
import RichText from '../RichTextV2';
import InlineLink from '../InlineLink';

const Section = styled.section`
  background-color: ${themeGet('colors.stone')};
  padding: 40px 0;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    padding: 60px 0;
  }
`;

const CoverageTitleWrapper = styled.div`
  margin: 0 0 30px 0;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    margin: 0 0 40px 0;
  }
`;

const Heading = styled.h2`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.heading2.fontWeight')};
  font-size: ${themeGet('typography.mobile.heading2.fontSize')};
  line-height: ${themeGet('typography.mobile.heading2.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.heading2.letterSpacing')};
  text-align: left;
  margin: 0 0 20px 0;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.heading2.fontSize')};
    line-height: ${themeGet('typography.desktop.heading2.lineHeight')};
    text-align: center;
  }
`;

const SubHeading = styled.p`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.body2.fontWeight')};
  font-size: ${themeGet('typography.mobile.body2.fontSize')};
  line-height: ${themeGet('typography.mobile.body2.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.body2.letterSpacing')};
  text-align: left;
  margin-bottom: 30px;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.body1.fontSize')};
    line-height: ${themeGet('typography.desktop.body1.lineHeight')};
    text-align: center;
    margin-bottom: 40px;
  }
`;

const ListItemHeading = styled.h3`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.heading4.fontWeight')};
  font-size: ${themeGet('typography.mobile.heading4.fontSize')};
  line-height: ${themeGet('typography.mobile.heading4.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.heading4.letterSpacing')};
  margin: 0 0 20px 0;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.heading3.fontSize')};
    line-height: ${themeGet('typography.desktop.heading3.lineHeight')};
    margin: 0 0 40px 0;
  }
`;

const StyledLi = styled.li`
  background-color: ${themeGet('colors.white')};
  border-radius: 12px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  margin: 20px 0;

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    margin: 40px 0;
  }
`;

const CoverageElement = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    padding: 30px;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding-bottom: 7px;
`;

const Icon = styled.img`
  object-fit: contain;
  width: 30px;
  height: 30px;
  margin: 0 10px 0 0;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    width: 40px;
    height: 40px;
    margin: 0 20px 0 0;
  }
`;

const ElementTitle = styled.h3`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.heading3.fontWeight')};
  font-size: ${themeGet('typography.mobile.body2.fontSize')};
  line-height: ${themeGet('typography.mobile.body2.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.body2.letterSpacing')};

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.heading4.fontSize')};
    line-height: ${themeGet('typography.desktop.heading4.lineHeight')};
  }
`;

const BodyCopy = styled.div`
  border-top: 1px solid #e8e9eb;
  padding: 10px 0 0;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    margin-left: calc(30px + 10px);
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    margin-left: calc(40px + 20px);
  }
`;

const ElementText = styled.p`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.body3.fontWeight')};
  font-size: ${themeGet('typography.mobile.body3.fontSize')};
  line-height: ${themeGet('typography.mobile.body3.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.body3.letterSpacing')};

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.body2.fontSize')};
    line-height: ${themeGet('typography.desktop.body2.lineHeight')};
  }
`;

const introParagraph = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <SubHeading>{children}</SubHeading>,
    [INLINES.HYPERLINK]: (node, children) => (
      <InlineLink href={node.data.uri}>{children}</InlineLink>
    ),
  },
};

const bodyCopy = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <ElementText>{children}</ElementText>,
    [INLINES.HYPERLINK]: (node, children) => (
      <InlineLink href={node.data.uri}>{children}</InlineLink>
    ),
  },
};

const InsuranceCoverage = ({ data }) => {
  const {
    sectionId,
    iconRowItems,
    headingText: headingRTF,
    paragraphRTF,
    iconRowItemsHeading,
  } = data;

  return (
    <Section id={sectionId}>
      <SectionContainer>
        {(headingRTF || paragraphRTF) && (
          <CoverageTitleWrapper>
            {headingRTF && <Heading>{headingRTF}</Heading>}
            {paragraphRTF && (
              <RichText document={paragraphRTF} options={introParagraph} />
            )}
          </CoverageTitleWrapper>
        )}
        <div>
          <ListItemHeading>{iconRowItemsHeading}</ListItemHeading>
          <ul>
            {iconRowItems.map(({ id, iconDam, headingText, bodyRTF }) => (
              <StyledLi key={id}>
                <CoverageElement>
                  <TitleWrapper>
                    <Icon
                      src={iconDam.asset.localImage.publicURL}
                      alt={iconDam.altText}
                    />
                    <ElementTitle>{headingText}</ElementTitle>
                  </TitleWrapper>
                  <BodyCopy>
                    <RichText document={bodyRTF} options={bodyCopy} />
                  </BodyCopy>
                </CoverageElement>
              </StyledLi>
            ))}
          </ul>
        </div>
      </SectionContainer>
    </Section>
  );
};

InsuranceCoverage.propTypes = {
  data: PropTypes.oneOfType([PropTypes.shape(), PropTypes.bool]),
};

InsuranceCoverage.defaultProps = {
  data: null,
};

export default InsuranceCoverage;
